<template>
  <FeatureGuard feature="solutions">
    <c-box class="viewsolutionlisting_section" mt="4xl">
      <c-box v-if="loadingFailed" class="note">
        {{ $t("solutions.loadingListing.failed") }}<br />
        <a v-if="!listName" href="#" @click.stop.prevent="loadData">{{
          $t("solutions.loadingFailed.retryLink")
        }}</a>
      </c-box>
      <c-box v-else-if="loading" class="note">
        {{ $t("solutions.loadingListing.processing") }}
      </c-box>
      <c-box v-else>
        <c-box class="row">
          <c-box class="col-md-12">
            <c-box class="breadcrumb-container">
              <Breadcrumb :path="getBreadCrumbs()"></Breadcrumb>
            </c-box>
          </c-box>
          <c-box class="col-md-12">
            <c-box
              :class="!$route.params.solutionId && 'border-box'"
              border-radius="base"
              box-shadow="base"
              bg="white"
              :px="{ base: '15px', md: 'lg', lg: '3xl', xl: '4xl' }"
              :py="{ base: 'lg', md: 'lg', lg: 'xl', xl: '2.5rem' }"
            >
              <template v-if="!$route.params.solutionId">
                <h1 class="box-header">{{ title }}</h1>
                <span
                  v-if="(listName || categoryIdStr || folderIdStr) && description"
                  class="description"
                >
                  {{ description }}
                </span>
              </template>

              <router-view :list-items="listItems" :parent-title="getLastBreadCrumbName()">
              </router-view>
            </c-box>
          </c-box>
        </c-box>
      </c-box>
    </c-box>
  </FeatureGuard>
</template>

<script lang="ts">
import FeatureGuard from "@/components/FeatureGuard.vue";
import { BreadcrumbItem } from "@/components/solutions/breadcrumb-item";
import Breadcrumb from "@/components/solutions/Breadcrumb.vue";
import { isConcealedCategory, isVideo } from "@/util/SolutionUtil";
import { CBox } from "@chakra-ui/vue";
import {
  ISolutionArticle,
  ISolutionArticleSearchResult,
  ISolutionCategoryStructure,
  ISolutionFolderStructure,
} from "@iloq/shared";
import { isArray, isEmpty } from "lodash";
import { defineComponent } from "vue";
import { mapState } from "vuex";

interface ListItem {
  title: string;
  href: string;
  type: string;
}

export default defineComponent({
  name: "SolutionListContainer",
  components: {
    Breadcrumb,
    FeatureGuard,
    CBox,
  },
  props: {
    listName: String,
    categoryIdStr: String,
    folderIdStr: String,
    solutionId: String,
  },
  data(): any {
    return {
      categoryId: undefined as number | undefined,
      folderId: undefined as number | undefined,
      title: "",
      description: "",
      listItems: [] as ListItem[],
      loading: false,
      loadingFailed: false,
    };
  },
  computed: {
    ...mapState("solutions", {
      structure: "structure" as any,
      allHotTopics: "hotTopics" as any,
    }),
  },
  watch: {
    categoryIdStr: "initData",
    folderIdStr: "initData",
    listName: "initData",
    allHotTopics: "initData",
  },
  methods: {
    pickCategoriesToList() {
      // For now, user should not be able to view categories list (maybe later?)
      // this.listItems = this.structure!.categories.map((category: ISolutionCategoryStructure) => ({
      //   title: category.name,
      //   href: `/support/solutions/categories/${category.id}`,
      //   type: 'category',
      // }));
      // this.title = this.$t('solutions.article.breadcrumb.root').toString();
      this.$router.push({ path: "/support/solutions" });
    },
    pickFoldersToList() {
      if (!this.solutionId && isConcealedCategory(this.categoryId)) {
        this.$router.push({ path: "/support/solutions" });
      } else {
        const category = this.structure!.categories.find(
          (c: ISolutionCategoryStructure) => c.id === this.categoryId
        );
        if (category) {
          this.listItems = category.folders.map((folder: ISolutionFolderStructure) => ({
            title: folder.name,
            href: `/support/solutions/categories/${category.id}/${folder.id}`,
            type: "folder",
          }));
        } else {
          this.listItems = [];
        }

        this.title = category?.name ?? "(Unknown category)";
        this.description = category?.description ?? "";
      }
    },
    pickArticlesToList(articles: ISolutionArticle[]) {
      const category = this.structure!.categories.find(
        (c: ISolutionCategoryStructure) => c.id === this.categoryId
      );
      const folder = category
        ? category.folders.find((f: ISolutionFolderStructure) => f.id === this.folderId)
        : undefined;

      this.listItems = articles.map((article: ISolutionArticle) => ({
        title: article.title,
        href: `/support/solutions/categories/${article.categoryId}/${article.folderId}/${article.freshdeskId}`,
        type: this.getType(article),
      }));

      this.title = folder?.name ?? "(Unknown folder)";
      this.description = folder?.description ?? "";
    },
    getBreadCrumbs(): BreadcrumbItem[] {
      const crumbs = this.$router.currentRoute?.matched
        ?.filter((route: any) => !isEmpty(route.meta?.breadCrumb))
        .flatMap((route: any) => {
          if (isArray(route.meta?.breadCrumb)) {
            return route.meta?.breadCrumb.map(this.getBreadCrumbsWithValues);
          } else {
            return this.getBreadCrumbsWithValues(route.meta?.breadCrumb);
          }
        });

      return crumbs;
    },
    getLastBreadCrumbName() {
      // Used for TOC heading in when viewing individual support articles
      const crumbs = this.getBreadCrumbs();
      if (crumbs) return crumbs[crumbs.length - 1]?.name;
    },
    getBreadCrumbsWithValues(breadCrumb: { name: string; href: string }) {
      // Replaces ID's in breadCrumbs with category names
      const path = {
        name: breadCrumb?.name,
        href: breadCrumb?.href,
      };

      if (breadCrumb?.name === "solutions-root") {
        path.name = this.$t("solutions.article.breadcrumb.root").toString();
      }

      const paramKeys = Object.keys(this.$router.currentRoute.params);
      if (isEmpty(paramKeys)) {
        return path;
      }

      for (const key of paramKeys) {
        if (breadCrumb?.name?.startsWith(":") && breadCrumb?.name?.includes(`:${key}`)) {
          const newName =
            key === "categoryIdStr"
              ? this.structure?.categories.find(
                  (category: any) =>
                    String(category.id) === String(this.$router.currentRoute.params[key])
                )?.name
              : key === "folderIdStr"
              ? this.structure?.categories
                  .find(
                    (category: any) =>
                      String(category.id) === String(this.$router.currentRoute.params.categoryIdStr)
                  )
                  ?.folders?.find(
                    (folder: any) =>
                      String(folder.id) === String(this.$router.currentRoute.params[key])
                  )?.name
              : path.name;
          if (newName) path.name = path.name.replace(`:${key}`, newName);
        }

        if (breadCrumb?.href?.includes(`:${key}`)) {
          path.href = path.href.replace(`:${key}`, this.$router.currentRoute.params[key]);
        }
      }

      return path;
    },
    pickListArticlesToList() {
      if (this.listName === "hot-topics") {
        this.pickArticlesToList(this.allHotTopics);
        this.title = this.$t("solutions.frontpage.linkLists.hotTopicsHeader").toString();
        this.description = this.$t("solutions.hotTopics.description").toString();
      } else {
        // Unknown list
        this.loadingFailed = true;
      }
    },
    getType(article: ISolutionArticle) {
      return isVideo(article) ? "video" : "article";
    },
    loadArticlesForFolder() {
      if (!this.solutionId && isConcealedCategory(this.categoryId)) {
        this.$router.push({ path: "/support/solutions" });
      } else {
        this.$store
          .dispatch("solutions/searchArticlesQuick", {
            folderId: this.folderId,
            sort: "orderInFolder asc",
            top: 1000,
          })
          .then((results: ISolutionArticleSearchResult) => {
            this.pickArticlesToList(results.value);
            this.loading = false;
          })
          .catch(() => {
            this.loading = false;
            this.loadingFailed = true;
          });
      }
    },
    loadData() {
      this.loadingFailed = false;
      this.loading = true;

      if (this.structureMissing()) {
        this.$store
          .dispatch("solutions/fetchStructure")
          .then(() => {
            if (this.folderId) {
              this.loadArticlesForFolder();
            } else if (this.categoryId) {
              this.pickFoldersToList();
              this.loading = false;
            } else {
              this.pickCategoriesToList();
              this.loading = false;
            }
          })
          .catch(() => {
            this.loading = false;
            this.loadingFailed = true;
          });
      } else if (this.folderId) {
        this.loadArticlesForFolder();
      }
    },
    structureMissing() {
      return !this.structure || this.structure.categories.length === 0;
    },
    initData() {
      window.scrollTo(0, 0);
      if (this.listName) {
        this.pickListArticlesToList();
      } else {
        this.categoryId = this.categoryIdStr ? parseInt(this.categoryIdStr, 10) : undefined;
        this.folderId = this.folderIdStr ? parseInt(this.folderIdStr, 10) : undefined;
        if (this.folderId || this.structureMissing()) {
          this.loadData();
        } else if (this.categoryId) {
          this.pickFoldersToList();
        } else {
          this.pickCategoriesToList();
        }
      }
    },
  },
  mounted() {
    this.initData();
  },
});
</script>

<style lang="scss">
@import "../design-system/assets/tokens/tokens";
.viewsolutionlisting_section {
  .note {
    padding-top: 40px;
    font-size: 18px;
  }
  .breadcrumb-container {
    margin-bottom: 48px;
  }

  .border-box {
    .box-header {
      font-size: 32px;
      line-height: 40px;
      font-weight: $weight-bold;
      margin-top: 0;
      margin-bottom: 14px;
    }
    .description {
      font-size: 16px;
      font-weight: $weight-normal;
      color: $color-iloq-gray-dark;
    }
    .list {
      .list-item {
        padding: 24px 0 20px;
        a {
          text-decoration: none;
          font-size: 15px;
          font-weight: $weight-bold;
          .icon {
            display: inline-block;
            background: url("/img/components/icons/folder.svg") no-repeat scroll center center;
            background-size: 16px 16px;
            margin: 0 10px -3px 0;
            padding: 0;
            width: 16px;
            height: 16px;
            border: none;
            &.article {
              background: url("/img/components/icons/article.svg") no-repeat scroll center center;
            }
            &.video {
              background: url("/img/components/icons/video.svg") no-repeat scroll center center;
            }
          }
        }
        border-bottom: 1px solid $color-iloq-gray-light;
      }
      :last-child {
        border-bottom: none;
      }
    }

    font-size: $size-ms;
    .input___wrapper input {
      font-size: $size-ms;
    }
    .textarea textarea {
      font-size: $size-ms;
    }
  }
}
</style>
